<template>
  <div>
    <Transition name="fade">
      <div v-if="showModal" class="home-promo-modal-overlay">
        <div class="home-promo-modal-content">
          <!-- Close Button -->
          <button class="close-btn" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
  
          <!-- Header -->
          <div v-if="bannerImageUrl">
            <div class="modal-header-image">
              <img :src="bannerImageUrl" alt="Promo Banner" class="img-fluid" />
            </div>
            <div class="text-center pt-3 px-3">
              <h2 class="title" style="font-size: 1.5rem; font-weight: 700; color: #333; margin-bottom: 5px;">{{ config.title }}</h2>
              <p class="subtitle" style="color: #666; font-size: 0.9rem; margin: 0;">{{ config.description }}</p>
            </div>
          </div>
          <div v-else class="modal-header-custom" :style="{ background: headerGradient }">
             <!-- Fallback to gradient if no image -->
             <h2 class="title">{{ config.title || 'VapeBay' }}</h2>
             <p class="subtitle">{{ config.description || 'Spesial Promo' }}</p>
          </div>

          <!-- Scrollable Content -->
          <div class="modal-body-custom">
            <!-- Regular Vouchers -->
            <div class="voucher-list" v-if="vouchers.length > 0">
              <div
                v-for="(voucher, index) in vouchers"
                :key="index"
                class="voucher-card"
              >
                <div class="voucher-info">
                  <div class="voucher-code">{{ voucher.humanCode }}</div>
                  <div class="voucher-desc">{{ voucher.description }}</div>
                </div>
                <button 
                  type="button"
                  class="copy-btn" 
                  @click="copyCode(voucher.humanCode)"
                  :style="{ 
                    color: config.headerGradientStart || '#11998e', 
                    borderColor: config.headerGradientStart || '#11998e',
                    '--hover-bg': config.headerGradientEnd || '#38ef7d' 
                  }"
                >
                  Salin
                </button>
              </div>
            </div>

            <!-- Special Section -->
            <div class="special-section" v-if="specialVoucher">
              <div class="special-header">
                <h3>{{ config.specialPromoTitle || 'SPECIAL PROMO' }}</h3>
                <p v-if="config.specialPromoDescription">{{ config.specialPromoDescription }}</p>
              </div>
              <div class="voucher-card special">
                <div class="voucher-info">
                  <div class="voucher-code text-white">{{ specialVoucher.humanCode }}</div>
                  <div class="voucher-desc text-white-50">
                    {{ specialVoucher.description }}
                  </div>
                </div>
                <button
                  type="button"
                  class="copy-btn special-btn"
                  @click="copyCode(specialVoucher.humanCode)"
                >
                  Salin
                </button>
              </div>
            </div>
          </div>
  
          <!-- Footer -->
          <div class="modal-footer-custom">
            <p class="tnc-text">*Syarat dan ketentuan berlaku</p>
            <button class="shop-now-btn" @click="handleMainButtonClick" :style="{ background: buttonColor }">
              BELANJA SEKARANG
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Floating Trigger Button -->
    <div class="floating-promo-btn" @click="openModal" :style="{ background: headerGradient }">
      <i class="fas fa-gift"></i>
      <!-- <span class="btn-tooltip">Promo</span> -->
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed, nextTick } from 'vue';
import { toast } from 'vue3-toastify';
import { usePromotionStore } from '~/store/promotion';
import { AvailablePromotionItemDto } from '~/contract/dto/PromotionRelated.dto';

const showModal = ref(false);
const promotionStore = usePromotionStore();
const config = computed(() => promotionStore.popupConfig || {});

// Computed properties
const headerGradient = computed(() => {
  const start = config.value.headerGradientStart || '#11998e';
  const end = config.value.headerGradientEnd || '#38ef7d';
  return `linear-gradient(135deg, ${start}, ${end})`;
});

const bannerImageUrl = computed(() => {
  return config.value.bannerImage || null;
});

const buttonColor = computed(() => {
  if (config.value && config.value.buttonColor) {
    return config.value.buttonColor;
  }
  // Loop back header gradient if no specific button color is set
  return headerGradient.value; 
});

const specialVoucherCode = computed(() => config.value.specialVoucherCode || 'VAPEBAY10K');

const specialVoucher = computed(() => {
  if (!specialVoucherCode.value) return null;
  return promotionStore.promotionList.find(p => p.humanCode === specialVoucherCode.value);
});

const vouchers = computed(() => {
  let list = promotionStore.promotionList.filter(p => p.humanCode !== specialVoucherCode.value);
  
  let allowedCodes: string[] = [];
  const rawCodes = config.value.voucherCodes;

  if (rawCodes) {
      if (Array.isArray(rawCodes)) {
          allowedCodes = rawCodes;
      } else if (typeof rawCodes === 'string') {
          try {
              const parsed = JSON.parse(rawCodes);
              if (Array.isArray(parsed)) {
                  allowedCodes = parsed;
              }
          } catch (e) {
              console.error("Error parsing voucherCodes:", e);
          }
      }
  }

  if (allowedCodes.length > 0) {
     list = list.filter(p => allowedCodes.includes(p.humanCode));
  }
  
  return list;
});

// Consolidated logic to start the modal
const checkAndShowModal = () => {
  // Check config status if it exists
  if (config.value && config.value.isActive === false) return;

  if (promotionStore.promotionList.length > 0) {
      showModal.value = true;
  }
};

const initModal = async () => {
    try {
        await Promise.all([
            promotionStore.fetchAvailablePromotion(),
            promotionStore.fetchPopupConfig()
        ]);
    } catch (e) {
        console.error("Failed to fetch promo data", e);
    } finally {
        checkAndShowModal();
    }
}



// ... (existing imports)

onMounted(() => {
  nextTick(() => {
    initModal();
  });
});

const closeModal = () => {
  showModal.value = false;
};

const handleMainButtonClick = () => {
     if (config.value && config.value.redirectUrl) {
    window.location.href = config.value.redirectUrl;
  } else {
    closeModal();
  }
};

const openModal = async () => {
  await initModal(); 
  
  if (config.value.isActive === false) return;

  if (promotionStore.promotionList.length === 0) {
     toast.info('Tidak ada promo tersedia saat ini', {
      position: 'top-center',
      autoClose: 2000,
    });
  }
};
// ... (copyCode)
// ... (copyCode remain same)

const copyCode = async (code: string) => {
  try {
    await navigator.clipboard.writeText(code);
    toast.success('Kode berhasil disalin', {
      position: 'top-center',
      autoClose: 2000,
      hideProgressBar: true,
    });
  } catch (err) {
    console.error('Failed to copy (async): ', err);
    // Fallback for older browsers or non-secure contexts
    const textArea = document.createElement("textarea");
    textArea.value = code;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand('copy');
      if (successful) {
         toast.success('Kode berhasil disalin', {
          position: 'top-center',
          autoClose: 2000,
          hideProgressBar: true,
        });
      } else {
         toast.error('Gagal menyalin kode');
      }
    } catch (err) {
      console.error('Fallback copy failed', err);
      toast.error('Gagal menyalin kode');
    }
    document.body.removeChild(textArea);
  }
};
</script>

<style scoped lang="scss">
.home-promo-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
}

.home-promo-modal-content {
  background: white;
  width: 90%;
  max-width: 400px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  animation: slideUp 0.4s ease-out;
}

.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(255, 255, 255, 0.8);
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 20;
  transition: all 0.3s ease;
  backdrop-filter: blur(2px);

  i {
    font-size: 1.2rem;
  }

  &:hover {
    background: white;
    color: #333;
    transform: rotate(90deg);
    border-color: white;
  }
}

.modal-header-custom {
  background: linear-gradient(135deg, #11998e, #38ef7d); /* Green Gradient for Ramadan */
  padding: 30px 20px 20px;
  text-align: center;
  color: white;

  .title {
    font-size: 1.8rem;
    font-weight: 800;
    margin-bottom: 5px;
    letter-spacing: 0.5px;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .subtitle {
    font-size: 0.9rem;
    opacity: 0.95;
    margin: 0;
  }
}

.modal-header-image {
  width: 100%;
  max-height: 180px; 
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.modal-body-custom {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.voucher-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 25px;
}

.voucher-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 12px 15px;
  background: white;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);

  &.special {
    background: #212529;
    border-color: #333;

    .voucher-code {
      color: #ffc107;
    }
  }

  .voucher-info {
    flex: 1;
    padding-right: 10px;
  }

  .voucher-code {
    font-weight: 700;
    font-size: 1rem;
    color: #333;
    margin-bottom: 2px;
    letter-spacing: 0.5px;
  }

  .voucher-desc {
    font-size: 0.75rem;
    color: #666;
  }

  .copy-btn {
    background: transparent;
    border: 1px solid #11998e;
    color: #11998e;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: var(--hover-bg, #11998e);
      color: white;
      border-color: transparent;
    }

    &.special-btn {
      border-color: #ffc107;
      color: #ffc107;

      &:hover {
        background: #ffc107;
        color: #000;
      }
    }
  }

  .text-white {
    color: white !important;
  }
  .text-white-50 {
    color: rgba(255, 255, 255, 0.6) !important;
  }
}

.special-section {
  background: black;
  color: white;
  padding: 15px;
  border-radius: 12px;
  text-align: center;
  margin-top: 10px;

  .special-header {
    margin-bottom: 15px;

    h3 {
      font-size: 1rem;
      font-weight: 700;
      color: #ffc107; /* Gold/Yellowish */
      margin-bottom: 5px;
    }

    p {
      font-size: 0.75rem;
      color: rgba(255, 255, 255, 0.7);
      margin: 0;
    }
  }
}

.modal-footer-custom {
  padding: 15px 20px 25px;
  text-align: center;
  background: white;
  border-top: 1px solid #f0f0f0;

  .tnc-text {
    font-size: 0.7rem;
    color: #999;
    margin-bottom: 15px;
    font-style: italic;
  }

  .shop-now-btn {
    width: 100%;
    background: linear-gradient(135deg, #11998e, #38ef7d); /* Green Gradient matching header */
    color: white;
    border: none;
    padding: 14px;
    border-radius: 50px;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(17, 153, 142, 0.4);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(17, 153, 142, 0.5);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.floating-promo-btn {
  position: fixed;
  bottom: 100px;
  left: 25px;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #11998e, #38ef7d);
  border-radius: 50%;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 9;
  transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  animation: bounce-vertical 2s infinite ease-in-out;

  i {
    color: white;
    font-size: 1.5rem;
  }

  // ... (tooltip styles)

  &:hover {
    transform: scale(1.1);
    animation: none; // Stop pulsing on hover
    
    .btn-tooltip {
      opacity: 1;
      top: -40px;
    }
  }
}

@keyframes bounce-vertical {
  0%, 100% {
    transform: translateY(0);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
  50% {
    transform: translateY(-15px);
    box-shadow: 0 15px 25px rgba(0, 0, 0, 0.1);
  }
}
</style>
